import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  MoodleCourseTabKey,
  MoodleNavigationContextValue,
  MoodleViewState,
} from './types';

const MoodleNavigationContext = createContext<MoodleNavigationContextValue | null>(null);

interface Props {
  initialView?: MoodleViewState;
  onViewChange?: (view: MoodleViewState) => void;
  goBack?: () => void;
  children: React.ReactNode;
}

export function MoodleNavigationProvider({
                                           initialView = { type: 'course-list', listView: 'table' },
                                           onViewChange,
                                           goBack,
                                           children,
                                         }: Props) {
  const [internalView, setInternalView] = useState<MoodleViewState>(initialView);

  const setView = (view: MoodleViewState) => {
    setInternalView(view);
    onViewChange?.(view);
  };

  const value = useMemo<MoodleNavigationContextValue>(
    () => ({
      view: internalView,
      setView,
      goBack,

      openCourse: (courseId: number, tab: MoodleCourseTabKey = 'course-content') => {
        setView({ type: 'course-home', courseId, tab });
      },

      openCourseList: (listView: 'table' | 'tiles' = 'table') => {
        setView({ type: 'course-list', listView });
      },

      openEvent: (courseId: number, tab: MoodleCourseTabKey, eventId: number) => {
        setView({ type: 'event-detail', courseId, tab, eventId });
      },

      /**
       * Legacy activity route support.
       * Prefer openLearningItem for the unified learning shell.
       */
      openActivity: (courseId: number, moduleId: number) => {
        setView({ type: 'activity', courseId, moduleId });
      },

      /**
       * Unified learning shell entrypoint.
       * This is the preferred path for page/lesson/resource/url/forum/etc.
       */
      openLearningItem: (courseId: number, itemId: number) => {
        setView({ type: 'learning-content', courseId, itemId });
      },

      /**
       * Legacy lessons list support.
       * Can remain for compatibility while migrating to unified shell.
       */
      openLessons: (courseId: number) => {
        setView({ type: 'lessons', courseId });
      },

      /**
       * Legacy standalone lesson support.
       * Can remain for compatibility while migrating to unified shell.
       */
      openLesson: (courseId: number, lessonId: number) => {
        setView({ type: 'lesson', courseId, lessonId });
      },

      openQuizEntry: (courseId: number, quizId: number) => {
        setView({ type: 'quiz-entry', courseId, quizId });
      },

      openQuizAttempt: (courseId: number, quizId: number, attemptId: number) => {
        setView({ type: 'quiz-attempt', courseId, quizId, attemptId });
      },

      openQuizResult: (courseId: number, quizId: number, attemptId: number) => {
        setView({ type: 'quiz-result', courseId, quizId, attemptId });
      },

      openQuizReview: (courseId: number, quizId: number, attemptId: number) => {
        setView({ type: 'quiz-review', courseId, quizId, attemptId });
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalView, goBack]
  );

  return (
    <MoodleNavigationContext.Provider value={value}>
      {children}
    </MoodleNavigationContext.Provider>
  );
}

export function useMoodleNavigation() {
  const ctx = useContext(MoodleNavigationContext);
  if (!ctx) {
    throw new Error('useMoodleNavigation must be used within MoodleNavigationProvider');
  }
  return ctx;
}